/**
 * Componente: panel "Posición y tamaño" del Diseñador (como en CorelDRAW).
 *
 * Muestra X, Y, Ancho y Alto del/los elemento(s) seleccionado(s) en milímetros reales de la tarjeta, con el candado de
 * proporción y el botón "Foto 3:4". Con varios elementos seleccionados cada campo muestra "Mixto" si difieren, y lo que
 * se escribe se aplica a todos. Un elemento sin ancho/alto propios (texto de ancho automático) solo tiene X e Y: si todos
 * los seleccionados son así no se muestran Ancho ni Alto.
 *
 * No tiene lógica de geometría propia ni estado del diseño: lee con valorComunMm() y escribe a través de las funciones
 * que le pasa el editor (que usan aplicarMedida() / ajustarFoto34() de utils/posicionTamano.js).
 */

import React, { useState } from 'react';
import { Lock, Unlock } from 'lucide-react';
import CampoMedida from './CampoMedida';
import {
  valorComunMm,
  esQR,
  esFoto,
  esFoto34,
  MENSAJES_PARSEO,
  parsearMedida,
  formatearMedida,
} from '../utils/posicionTamano';

const PanelPosicionTamano = ({ elementos, dim, proporcionBloqueada, onCambiarCandado, onAplicarMedida, onFoto34 }) => {
  const [avisoFoto, setAvisoFoto] = useState('');
  if (!elementos || elementos.length === 0) return null;

  const x = valorComunMm(elementos, 'x', dim);
  const y = valorComunMm(elementos, 'y', dim);
  const ancho = valorComunMm(elementos, 'ancho', dim);
  const alto = valorComunMm(elementos, 'alto', dim);
  const muestraTamano = ancho.aplica || alto.aplica;

  const todosQR = elementos.every(esQR);
  const hayQR = elementos.some(esQR);
  const fotos = elementos.filter(esFoto);
  const candadoFijo = todosQR; // el QR siempre es cuadrado
  const candadoActivo = candadoFijo || proporcionBloqueada;
  const varios = elementos.length > 1;
  const prefijo = varios ? 'medida-multi' : 'medida';

  // Convierte el texto escrito en un número y delega en el editor. Devuelve el aviso a mostrar (o null).
  const confirmar = (campo) => (texto) => {
    const r = parsearMedida(texto);
    if (!r.ok) return MENSAJES_PARSEO[r.motivo];
    return onAplicarMedida(campo, r.valor);
  };

  const aplicarFoto = () => {
    setAvisoFoto(onFoto34() || '');
  };
  const todasFoto34 = fotos.length > 0 && fotos.every((f) => esFoto34(f, dim));

  return (
    <div className="panel-posicion-tamano" role="group" aria-label="Posición y tamaño en milímetros">
      <h4 className="panel-posicion-titulo">Posición y tamaño <span>(mm sobre la tarjeta de {formatearMedida(dim.anchoMm)} × {formatearMedida(dim.altoMm)})</span></h4>

      <div className="panel-posicion-grilla">
        <CampoMedida id={`${prefijo}-x`} etiqueta="X" valorMm={x.valorMm} mixto={x.mixto} onConfirmar={confirmar('x')} />
        <CampoMedida id={`${prefijo}-y`} etiqueta="Y" valorMm={y.valorMm} mixto={y.mixto} onConfirmar={confirmar('y')} />
        {muestraTamano && (
          <>
            <CampoMedida id={`${prefijo}-ancho`} etiqueta="Ancho" valorMm={ancho.valorMm} mixto={ancho.mixto} deshabilitado={!ancho.aplica} onConfirmar={confirmar('ancho')} />
            <CampoMedida id={`${prefijo}-alto`} etiqueta="Alto" valorMm={alto.valorMm} mixto={alto.mixto} deshabilitado={!alto.aplica} onConfirmar={confirmar('alto')} />
          </>
        )}
      </div>

      {muestraTamano ? (
        <div className="panel-posicion-acciones">
          <button
            type="button"
            className={`canvas-toggle-btn panel-posicion-candado ${candadoActivo ? 'activo' : ''}`}
            onClick={onCambiarCandado}
            disabled={candadoFijo}
            aria-pressed={candadoActivo}
            aria-label="Mantener la proporción entre ancho y alto"
            title={candadoFijo
              ? 'El código QR siempre se mantiene cuadrado: si no, no escanea.'
              : (candadoActivo ? 'Proporción bloqueada: al cambiar el ancho o el alto, el otro acompaña' : 'Bloquear la proporción')}
          >
            {candadoActivo ? <Lock size={16} /> : <Unlock size={16} />}
          </button>
          <span className="panel-posicion-candado-texto">
            {candadoFijo
              ? 'El QR siempre queda cuadrado.'
              : (candadoActivo
                ? 'Proporción bloqueada: el ancho y el alto cambian juntos.'
                : 'Proporción libre: ancho y alto se cambian por separado.')}
            {hayQR && !candadoFijo ? ' Los QR de la selección igual quedan cuadrados.' : ''}
          </span>
        </div>
      ) : (
        <p className="form-hint">Este texto tiene ancho automático: solo se fija su posición (X e Y).</p>
      )}

      {fotos.length > 0 && (
        <div className="panel-posicion-foto">
          <button
            type="button"
            className="panel-posicion-foto34"
            onClick={aplicarFoto}
            disabled={todasFoto34}
            title="Deja la caja de la foto en proporción 3:4 (retrato) manteniendo el ancho"
          >
            Foto 3:4{fotos.length > 1 ? ` (${fotos.length} fotos)` : ''}
          </button>
          <span className="form-hint">
            {todasFoto34
              ? 'La caja ya está en 3:4 (así la foto del carnet no se recorta).'
              : 'Deja la caja en proporción 3:4 manteniendo el ancho: la foto del carnet se dibuja centrada y recortada para llenar la caja.'}
          </span>
          {avisoFoto && <span className="campo-medida-aviso" role="status">{avisoFoto}</span>}
        </div>
      )}

      {varios && (
        <p className="form-hint">Lo que escribas se aplica a los {elementos.length} elementos seleccionados (“Mixto” = hoy no coinciden).</p>
      )}
    </div>
  );
};

export default PanelPosicionTamano;
